import { Component, Input, OnChanges, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { appColors } from '../../../core/styles/colors';
import { EChartsOption, SeriesOption } from 'echarts';
import { DateTime } from 'luxon';
import { DateUtils } from '../../../core/utilities/date-time-helpers';
import { chartDefaultOptions, chartExpandIcon, getWeekendsArea } from '../../../core/styles/chart-defauls';
import { MatDialog } from '@angular/material/dialog';
import { MatFaxChartZoomDialogComponent } from './mat-fax-chart-zoom-dialog.component';
import { NgxEchartsDirective } from 'ngx-echarts';

@Component({
  selector: 'app-fax-chart-by-time',
  templateUrl: './chart-by-time.component.html',
  standalone: true,
  imports: [NgxEchartsDirective],
})
export class FaxChartByTimeComponent implements OnInit, OnDestroy, OnChanges {
  @Input() showExpand = false;
  @Input() startDate: string;
  @Input() endDate: string;
  @Input() data: any[] = [];
  @Input() chartType: 'line' | 'bar' = 'line';

  @ViewChild('expandBtn') expandBtn;

  colors = appColors;

  updateOptions: EChartsOption  = {};

  initOptions = {};

  options: EChartsOption  = {
    title: Object.assign({}, chartDefaultOptions.title, {
      text: 'Message Breakdown',
    }),
    legend: Object.assign({}, chartDefaultOptions.legend),
    grid: Object.assign({}, chartDefaultOptions.grid),
    dataset: [
      {
        dimensions: [
          { name: 'day', type: 'time' },
          { name: 'inboundFax', type: 'number' },
          { name: 'outboundFax', type: 'number' },
          { name: 'failedFax', type: 'number' },
        ],
        source: [],
      },
    ],
    toolbox: [
      {
        show: true,
        feature: {
          dataZoom: {
            yAxisIndex: 'none',
          },
          saveAsImage: {},
          myZoom: {
            show: this.showExpand,
            title: 'Expand',
            icon: chartExpandIcon,
            onclick: () => {
              console.log('on expand click');
              this.expandBtn.nativeElement.click();
            },
          },
        },
      },
    ],
    xAxis: {
      type: 'time',
      boundaryGap: ['20%', '20%'],
    },
    yAxis: {
      type: 'value',
      scale: true,
      min: 0,
      axisLine: {
        show: true,
      },
      name: 'Total count',
      nameGap: 30,
      nameRotate: 90,
      nameLocation: 'middle',
    },
    dataZoom: [
      {
        type: 'inside',
        filterMode: 'none',
        minValueSpan: 3600 * 24 * 5 * 1000,
        start: 0,
        end: 100,
      },
      {
        start: 0,
        end: 100,
        labelFormatter: function (value: number) {
          const dateTime = DateTime.fromMillis(value);
          return dateTime.toFormat('MM/dd/yyyy');
        },
      },
    ],
    series: [
      {
        type: 'line',
        name: 'Incoming FAX',
        color: appColors.primaryGreen,
        smooth: true,
        // stack: 'calls',
        sampling: 'sum',
        showSymbol: false,
        smoothMonotone: 'x',
        encode: {
          x: ['day'],
          y: ['inboundFax'],
          itemName: ['inboundFax'],
        },
      },
      {
        type: 'line',
        name: 'Outgoing FAX',
        color: appColors.primaryBlue,
        smooth: true,
        // stack: 'calls',
        sampling: 'sum',
        showSymbol: false,
        smoothMonotone: 'x',
        encode: {
          x: ['day'],
          y: ['outboundFax'],
          itemName: ['outboundFax'],
        },
      },
      {
        type: 'line',
        name: 'Failed FAX',
        color: appColors.primaryRed,
        smooth: true,
        // stack: 'calls',
        sampling: 'sum',
        showSymbol: false,
        smoothMonotone: 'x',
        encode: {
          x: ['day'],
          y: ['failedFax'],
          itemName: ['failedFax'],
        },
      },
    ],
    tooltip: {
      show: true,
      trigger: 'axis',
      axisPointer: {
        label: {
          formatter: params => {
            const dateTime = DateUtils.localDateToDateTime(new Date(params.value as number));
            // const dateTime = DateTime.fromMillis(params.value as number);
            return dateTime.toFormat('MM/dd/yyyy EEE');
          },
        },
      },
    },
  };

  private chart: any;

  constructor(public dialog: MatDialog) {
    const resizeObserver = new ResizeObserver(() => {
      this.resizeChart();
    });
    resizeObserver.observe(document.body);
  }

  ngOnChanges() {
    console.log('my on ngOnChanges');

    this.updateSeries();

    this.data.sort((a, b) => {
      const aTime = new Date(a.date);
      const bTime = new Date(b.date);
      return aTime > bTime ? -1 : aTime < bTime ? 1 : 0;
    });

    this.updateChart();
  }

  updateChart() {
    const groupedData = [...this.data];

    const dataset = Object.assign({}, this.options.dataset[0], {
      source: groupedData,
    });
    this.options.dataset[0] = dataset;

    const currentOptions = this.chart ? this.chart.getOption() : this.options.dataZoom;
    if (this.chart) {
      currentOptions.dataZoom[0].filterMode = this.options.dataZoom[0].filterMode;
    }

    this.options.toolbox[0].feature.myZoom.show = this.showExpand;

    const startRange = DateTime.fromISO(this.startDate).plus({ day: -1 }).toJSDate();
    const endRange = DateTime.fromISO(this.endDate).plus({ day: 1 }).toJSDate();
    const markArea = getWeekendsArea(startRange, endRange);
    for (const series of this.options.series as Array<SeriesOption>) {
      series.markArea = markArea;
    }

    this.updateOptions = {
      toolbox: this.options.toolbox,
      series: this.options.series,
      dataset: dataset,
      dataZoom: currentOptions.dataZoom,
      xAxis: {
        min: startRange,
        max: endRange,
      },
    };
  }

  ngOnInit() {
    console.log('my on init');
  }

  resizeChart() {
    if (this.chart) {
      this.chart.resize();
    }
  }

  ngOnDestroy() {}

  onChartInit($event: any) {
    this.chart = $event;
    console.log('on chart init:', $event);
  }

  updateSeries() {
    console.log('update series');
    for (const item of this.options.series as SeriesOption[]) {
      item.type = this.chartType;
      item.universalTransition = true;

      if (item.type === 'bar') {
        item.stack = 'calls';
        item.barMaxWidth = 50;
        this.options.dataZoom[0].filterMode = 'filter';
      } else if (item.type === 'line') {
        item.stack = undefined;
        item.smooth = false;
        item.showSymbol = false;
        this.options.dataZoom[0].filterMode = 'none';
      }
    }
  }

  onChartMagicTypeChanged($event: any) {
    console.log($event);
    if ($event.currentType) {
      this.chartType = $event.currentType;
      this.updateSeries();
      this.updateChart();
    }
  }

  onExpandClick() {
    this.dialog.open(MatFaxChartZoomDialogComponent, {
      width: '90%',
      height: '90%',
      data: {
        chartData: this.data,
        chartType: this.chartType,
        startDate: this.startDate,
        endDate: this.endDate,
      },
    });
  }
}
